import { ErreurApi } from '../../api/client.js';
import type { DonneesPilotage } from './modele.js';
import type { SourcePilotage } from './source.js';

export interface EtatPilotage {
  statut: 'chargement' | 'pret' | 'erreur';
  donnees: DonneesPilotage | null;
  message: string | null;
  messageEstErreur: boolean;
  filtreStatut: string | null;
  /** Identifiants des décisions ou relances dont l'écriture est en cours (anti double envoi). */
  enCours: readonly string[];
}
export interface ControleurPilotage {
  etat: () => EtatPilotage;
  abonner: (ecouteur: () => void) => () => void;
  charger: () => Promise<void>;
  filtrerStatut: (code: string | null) => void;
  trancher: (decisionId: string, option: string) => Promise<void>;
  validerRelance: (messageId: string, decision: 'valider' | 'rejeter') => Promise<void>;
}

const MESSAGE_INCONNU = 'Une erreur est survenue de notre côté. Réessayez dans un instant ; si elle persiste, contactez le Cabinet MAAT.';
export const MESSAGE_DECISION_ENREGISTREE = 'Votre décision est enregistrée.';
export const MESSAGE_RELANCE_VALIDEE = 'La relance est validée : elle part depuis votre numéro.';
export const MESSAGE_RELANCE_REJETEE = 'La relance est rejetée : elle ne partira pas.';

const phrase = (erreur: unknown): string => (erreur instanceof ErreurApi ? erreur.messageUtilisateur : MESSAGE_INCONNU);

/** Contrôleur de D01 et M01 : état, chargement et écritures. Une écriture réussie retire la ligne et l'annonce. */
export function creerControleur(source: SourcePilotage): ControleurPilotage {
  let etat: EtatPilotage = { statut: 'chargement', donnees: null, message: null, messageEstErreur: false, filtreStatut: null, enCours: [] };
  const ecouteurs = new Set<() => void>();
  const changer = (suite: Partial<EtatPilotage>): void => {
    etat = { ...etat, ...suite };
    for (const e of ecouteurs) e();
  };

  /** Une écriture à la fois par identifiant ; `appliquer` est exécuté seulement en cas de succès. */
  async function ecrire(
    cle: string,
    ecriture: () => Promise<void>,
    succes: string,
    appliquer: (d: DonneesPilotage) => DonneesPilotage,
  ): Promise<void> {
    if (etat.enCours.includes(cle)) return;
    changer({ enCours: [...etat.enCours, cle], message: null, messageEstErreur: false });
    try {
      await ecriture();
    } catch (erreur) {
      changer({ enCours: etat.enCours.filter((c) => c !== cle), message: phrase(erreur), messageEstErreur: true });
      return;
    }
    changer({
      enCours: etat.enCours.filter((c) => c !== cle),
      donnees: etat.donnees ? appliquer(etat.donnees) : etat.donnees,
      message: succes,
      messageEstErreur: false,
    });
  }

  return {
    etat: () => etat,
    abonner(ecouteur) {
      ecouteurs.add(ecouteur);
      return () => {
        ecouteurs.delete(ecouteur);
      };
    },
    async charger() {
      changer({ statut: 'chargement', message: null, messageEstErreur: false });
      try {
        changer({ statut: 'pret', donnees: await source.charger() });
      } catch (erreur) {
        changer({ statut: 'erreur', donnees: null, message: phrase(erreur), messageEstErreur: true });
      }
    },
    filtrerStatut(code) {
      changer({ filtreStatut: code === null || code === etat.filtreStatut ? null : code });
    },
    async trancher(decisionId, option) {
      const decision = etat.donnees?.decisions.find((d) => d.id === decisionId);
      if (!decision || !decision.options.some((o) => o.code === option)) return;
      await ecrire(
        decisionId,
        async () => {
          await source.trancher(decisionId, option);
        },
        MESSAGE_DECISION_ENREGISTREE,
        (d) => ({
          ...d,
          decisions: d.decisions.filter((x) => x.id !== decisionId),
          tableau: { ...d.tableau, pendingDecisions: Math.max(0, d.tableau.pendingDecisions - 1) },
        }),
      );
    },
    async validerRelance(messageId, decision) {
      if (!etat.donnees?.relances.some((r) => r.id === messageId)) return;
      await ecrire(
        messageId,
        () => source.validerRelance(messageId, decision),
        decision === 'valider' ? MESSAGE_RELANCE_VALIDEE : MESSAGE_RELANCE_REJETEE,
        (d) => ({ ...d, relances: d.relances.filter((r) => r.id !== messageId) }),
      );
    },
  };
}
